import { useCallback, useEffect, useLayoutEffect, useRef, useState, type DependencyList } from 'react';
import { logBackgroundError } from '../observability';

/**
 * #665 (F02) — the one state machine of a read: the data, the loading flag,
 * the error, Retry, a refresh behind the page, and the newest run only.
 *
 * Before this hook, 22 read sites wrote the machine by hand, with five kinds
 * of race guard or none. The copies differed: a missing guard showed the late
 * answer of an older filter, a failed read showed an empty list, and a read
 * sent no telemetry (#457, #627, bugs 50 and 89 of #661).
 *
 *   const list = useLoad(() => getOfficials().then(r => r.data.officials), [user?.id],
 *     { context: 'Officials:list', initial: [] });
 *   <AsyncContent loading={list.loading} error={!!list.error} onRetry={list.reload} …>
 *
 * The rules:
 *   1. A run starts when the component mounts and when a value of `deps`
 *      changes. Put into `deps` each value that the read uses, as for
 *      `useEffect`. Each run reads the newest `read`.
 *   2. Only the NEWEST run writes the state. A run of the old `deps`, an older
 *      `reload()` and an older `refresh()` are dropped when they answer late.
 *      No run writes the state after the component unmounts.
 *   3. `read === null` closes the gate (no user, no id, a tab that is not
 *      open). Nothing runs, and `loading` is false. When the gate opens, a run
 *      starts. The gate is part of the key, as `deps` are.
 *   4. A new key (a change of `deps` or of the gate) clears the data to
 *      `initial` at once, in the same render. With `keepData: true` the data
 *      of the old key stays on screen until the new answer. Each new key
 *      clears the error and shows `loading`.
 *   5. `reload()` is Retry: it shows `loading`, clears the error and keeps the
 *      data. A failure sets `error`.
 *   6. `refresh()` reads behind the page: no `loading`, and the data and the
 *      error stay until the answer. A good answer replaces the data and clears
 *      the error. It runs `onData` too, so an `onData` that fills a form must
 *      skip the fill while the form has unsaved edits. A failure changes nothing and REJECTS, so the caller tells
 *      the user (`useAfterWrite()` logs `<source>:refresh` and shows the
 *      warning, #419). One exception: a refresh that starts during a shown run
 *      replaces that run, so its failure also ends the loading and sets `error`.
 *   7. `setData()` is a local write. A read that started before a local write
 *      does not write its answer: the hook reads again (frontend.md, Lessons:
 *      a late answer must not draw over a newer write).
 *   8. A failure of a run or of `reload()` goes to `logBackgroundError(context)`
 *      and to `error`. By default every failure is logged: a 404 on a list is
 *      a missing route after a deploy, not an answer. An error that the page
 *      shows as an answer, or that `onError` shows to the user (`showError()`
 *      records `ui_error_shown`), goes to `error` only: pass `expected`. A page
 *      that reads ONE record by an id from a link passes `expected: isNotFound`
 *      (a 404, or a 400 `INVALID_UUID`) and shows it through `LoadFailure`.
 *   9. The hook shows no toast. The page shows `error`: `AsyncContent`,
 *      `ErrorState` or `LoadFailure`.
 *
 * `onData` runs with each answer that the hook writes, also for `refresh()`, before the render (a
 * form prefill). It does not run for `setData()`. `onError` runs with each
 * failure of a run or of `reload()` that the hook writes: a picker shows the
 * failure inline (`inlineError()`) or as a toast (`showError()`), and these
 * calls must not run in render. It does not run for `refresh()`, which rejects.
 *
 * `reload()` and `refresh()` that run before the mount effect of the hook are
 * dropped (the hook is not alive yet). Call them from an event or an effect.
 */

export interface LoadOptions<T> {
  /** The name of the read in the telemetry: `<Component>:<what>`, for example `Athletes:list`. */
  context: string;
  /** A new key keeps the data of the old key until the new answer. The default (`false`) clears it to `initial`. */
  keepData?: boolean;
  /** An error that the page shows as an answer, not as a failure: it gets no telemetry. The default is `() => false`: every failure is logged. */
  expected?: (err: unknown) => boolean;
  /** Runs with each answer that the hook writes, before the render. */
  onData?: (data: T) => void;
  /** Runs with each failure of a run or of `reload()` that the hook writes, after the telemetry. */
  onError?: (err: unknown) => void;
}

export interface Load<D> {
  /** The answer of the newest run, or `initial` before the first answer and after a clear. */
  data: D;
  /** A local write of the data. A read that started before it reads again. */
  setData: (next: D | ((prev: D) => D)) => void;
  /** A shown run is in flight: the first run of a key, or `reload()`. */
  loading: boolean;
  /** The error of the last failed run, or `null`. Never a falsy value when a run failed. */
  error: unknown;
  /** Retry: read again and show `loading`. It never rejects. */
  reload: () => Promise<void>;
  /** Read again behind the page. It rejects when the read fails. */
  refresh: () => Promise<void>;
}

interface Slot<D> {
  /** The key (the gate and `deps`) that the state belongs to. */
  key: readonly unknown[];
  data: D;
  loading: boolean;
  error: unknown;
}

const sameKey = (a: readonly unknown[], b: readonly unknown[]) =>
  a.length === b.length && a.every((v, i) => Object.is(v, b[i]));

export function useLoad<T>(
  read: (() => Promise<T>) | null,
  deps: DependencyList,
  opts: LoadOptions<T> & { initial: NoInfer<T> },
): Load<T>;
export function useLoad<T>(
  read: (() => Promise<T>) | null,
  deps: DependencyList,
  opts: LoadOptions<T>,
): Load<T | undefined>;
export function useLoad<T>(
  read: (() => Promise<T>) | null,
  deps: DependencyList,
  opts: LoadOptions<T> & { initial?: T },
): Load<T | undefined> {
  type D = T | undefined;
  const gated = read === null;
  const key = [gated, ...deps];
  // The first `initial` only: a literal `[]` is a new array at each render.
  const initial = useRef<D>(opts.initial).current;
  const [slot, setSlot] = useState<Slot<D>>(() => ({ key, data: initial, loading: !gated, error: null }));

  // The props of the newest committed render. A run reads them after each `await`.
  const latest = useRef({ read, opts });
  useLayoutEffect(() => { latest.current = { read, opts }; });
  /** The ticket of the newest run. A run writes the state only while it holds the newest ticket. */
  const runs = useRef(0);
  /** The count of the local writes (`setData`). */
  const writes = useRef(0);
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const run = useCallback(async (ticket: number, shown: boolean): Promise<void> => {
    const newest = () => alive.current && ticket === runs.current;
    for (;;) {
      const fn = latest.current.read;
      if (!fn) return;
      const writesAtStart = writes.current;
      let value: T;
      try {
        value = await fn();
      } catch (err) {
        if (!newest()) return;
        const error = err || new Error(`${latest.current.opts.context}: the read failed`);
        if (!shown) {
          // Rule 6: a refresh changes nothing, except when it replaced a shown run.
          setSlot(s => (s.loading ? { ...s, loading: false, error } : s));
          throw err;
        }
        const { context, expected, onError } = latest.current.opts;
        if (!expected?.(err)) logBackgroundError(context)(err);
        setSlot(s => ({ ...s, loading: false, error }));
        onError?.(err);
        return;
      }
      if (!newest()) return;
      // Rule 7: a local write came after this read started. Read again.
      if (writes.current !== writesAtStart) continue;
      latest.current.opts.onData?.(value);
      setSlot(s => ({ ...s, data: value, loading: false, error: null }));
      return;
    }
  }, []);

  // A run for each new key. A new ticket also drops the run of the old key when the gate closes.
  useEffect(() => {
    const ticket = ++runs.current;
    const keep = latest.current.opts.keepData ?? false;
    setSlot(s => ({
      key,
      data: keep || sameKey(s.key, key) ? s.data : initial,
      loading: !gated,
      error: null,
    }));
    if (!gated) void run(ticket, true);
  }, key);

  const reload = useCallback(async () => {
    if (!alive.current || !latest.current.read) return;
    const ticket = ++runs.current;
    setSlot(s => ({ ...s, loading: true, error: null }));
    await run(ticket, true);
  }, [run]);

  const refresh = useCallback(async () => {
    if (!alive.current || !latest.current.read) return;
    await run(++runs.current, false);
  }, [run]);

  const setData = useCallback((next: D | ((prev: D) => D)) => {
    writes.current++;
    setSlot(s => ({ ...s, data: typeof next === 'function' ? (next as (prev: D) => D)(s.data) : next }));
  }, []);

  // Rule 4 in the render of the new key, before the effect runs: no frame
  // shows the old data with no loading flag (the empty-state flash of PR #94).
  const fresh = sameKey(slot.key, key);
  const keep = opts.keepData ?? false;
  return {
    data: fresh || keep ? slot.data : initial,
    setData,
    loading: fresh ? slot.loading : !gated,
    error: fresh ? slot.error : null,
    reload,
    refresh,
  };
}
